<script setup>
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { formatTime } from 'shared/helpers/formatTime';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';

const router = useRouter();
const route = useRoute();
const { contentAttributes } = useMessageContext();

const attrs = computed(() => contentAttributes.value || {});

const formatPrice = amount => {
  if (amount == null) return null;
  const currency = attrs.value.currency || 'EUR';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDay = computed(() => {
  if (!attrs.value.date) return '?';
  return new Date(attrs.value.date + 'T00:00:00').getDate();
});

const formatMonth = computed(() => {
  if (!attrs.value.date) return '';
  return new Date(attrs.value.date + 'T00:00:00').toLocaleDateString(
    undefined,
    { month: 'short' }
  );
});

const formattedTime = computed(() => {
  const startAt = attrs.value.startAt || attrs.value.start_at;
  if (!startAt) return null;
  const startStr = formatTime(startAt);
  const endAt = attrs.value.endAt || attrs.value.end_at;
  if (!endAt) return startStr;
  return `${startStr} – ${formatTime(endAt)}`;
});

const formattedHours = computed(() => {
  const h = attrs.value.hours;
  if (!h) return null;
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  if (mins === 0) return `${hours}h`;
  return `${hours}h${String(mins).padStart(2, '0')}`;
});

const eventId = computed(() => attrs.value.eventId || attrs.value.event_id);

const metadata = computed(() => {
  const items = [];
  if (formattedTime.value) {
    items.push({ icon: 'i-lucide-clock', value: formattedTime.value });
  }
  if (formattedHours.value) {
    items.push({ icon: 'i-lucide-timer', value: formattedHours.value });
  }
  const spaceName = attrs.value.spaceName || attrs.value.space_name;
  if (spaceName) {
    items.push({ icon: 'i-lucide-map-pin', value: spaceName });
  }
  if (attrs.value.guests) {
    items.push({
      icon: 'i-lucide-users',
      value: `${attrs.value.guests} guests`,
    });
  }
  const totalPrice = attrs.value.totalPrice || attrs.value.total_price;
  if (totalPrice) {
    items.push({
      icon: 'i-lucide-euro',
      value: formatPrice(totalPrice),
      class: 'font-medium text-n-slate-12',
    });
  }
  return items;
});

const navigateToEvent = () => {
  const id = eventId.value;
  if (!id) {
    // eslint-disable-next-line no-console
    console.warn(
      '[EventSummary] No event_id in content_attributes:',
      contentAttributes.value
    );
    return;
  }
  const accountId = route.params.accountId;
  router.push({
    name: 'planning_events_show',
    params: { accountId, eventId: id },
  });
};
</script>

<template>
  <BaseBubble class="p-0 overflow-hidden" data-bubble-name="event-summary">
    <div
      class="flex items-center gap-4 p-4 transition-colors"
      :class="eventId ? 'cursor-pointer hover:bg-n-alpha-1' : ''"
      @click="navigateToEvent"
    >
      <!-- Date Badge -->
      <div
        class="flex flex-col items-center justify-center w-14 h-14 bg-n-violet-3 rounded-lg shrink-0"
      >
        <span v-if="formatMonth" class="text-xs font-medium text-n-violet-11">
          {{ formatMonth }}
        </span>
        <span
          class="font-bold text-n-violet-12"
          :class="formatMonth ? 'text-lg' : 'text-xs'"
        >
          {{ formatDay }}
        </span>
      </div>

      <!-- Event Details -->
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-n-slate-12 truncate !mb-0">
          {{ attrs.experienceName || attrs.experience_name }}
        </p>
        <div
          v-if="metadata.length > 0"
          class="flex flex-col gap-0.5 text-xs text-n-slate-11 mt-1"
        >
          <div
            v-for="(item, index) in metadata"
            :key="index"
            class="flex items-center gap-1.5"
            :class="item.class"
          >
            <Icon :icon="item.icon" class="size-3" />
            <span :class="item.textClass">{{ item.value }}</span>
          </div>
        </div>
      </div>

      <!-- Chevron -->
      <Icon
        v-if="eventId"
        icon="i-lucide-chevron-right"
        class="size-4 text-n-slate-9 shrink-0"
      />
    </div>
  </BaseBubble>
</template>
